import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, FlaskConical, ShieldCheck, Beaker } from "lucide-react";
import { useCart } from "../context/CartContext";
import { toast } from "sonner";

const CATEGORY_LABELS = { peptides: "Peptides", blends: "Blends", supplies: "Supplies" };

const formatPrice = (n) => `$${n.toFixed(2)}`;

const ProductCard = ({ product }) => {
  const { addItem } = useCart();
  const v = product.variants[0];
  const minPrice = Math.min(...product.variants.map((x) => x.price));
  const maxPrice = Math.max(...product.variants.map((x) => x.price));
  const compare = v.compareAt;
  const discount =
    compare && compare > v.price
      ? Math.round(((compare - v.price) / compare) * 100)
      : 0;

  const handleAdd = (e) => {
    e.preventDefault();
    e.stopPropagation();
    addItem(product, v, 1);
    toast.success(`${product.name} · ${v.size} added to cart`);
  };

  return (
    <Link
      to={`/product/${product.id}`}
      className="product-card-clinical group relative flex flex-col bg-white"
    >
      <div className="relative aspect-square bg-white overflow-hidden border border-gray-100 group-hover:border-gray-300 transition-colors">
        {discount > 0 && (
          <div className="absolute top-3 left-3 z-20 text-[10px] font-semibold tracking-[0.18em] uppercase text-[#b91c1c]">
            — {discount}% off
          </div>
        )}
        {product.badge && (
          <div className="absolute top-3 right-3 z-20 text-[9px] font-semibold tracking-[0.2em] uppercase text-gray-500">
            {product.badge}
          </div>
        )}
        <img
          src={product.image}
          alt={product.name}
          className="absolute inset-0 w-full h-full object-contain p-6 transition-all duration-500 group-hover:scale-[1.04] group-hover:opacity-30"
          loading="lazy"
        />

        {/* Clinical hover info card */}
        <div className="absolute inset-0 z-10 flex flex-col justify-center p-6 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none">
          <div className="text-[10px] font-semibold tracking-[0.25em] uppercase text-[#b91c1c] mb-3 flex items-center gap-2">
            <div className="h-px flex-1 bg-[#b91c1c]/30" />
            Research Profile
            <div className="h-px flex-1 bg-[#b91c1c]/30" />
          </div>
          <ul className="space-y-2.5">
            {(product.benefits || []).slice(0, 4).map((b) => (
              <li key={b} className="flex items-start gap-2 text-[12px] text-[#0a0a0a] leading-snug">
                <span className="h-1 w-1 rounded-full bg-[#b91c1c] mt-1.5 shrink-0" />
                <span className="font-medium">{b}</span>
              </li>
            ))}
          </ul>
          <div className="mt-4 pt-4 border-t border-gray-200 grid grid-cols-3 gap-2">
            <div className="flex flex-col items-center text-center">
              <FlaskConical className="h-3.5 w-3.5 text-[#b91c1c] mb-1" />
              <span className="text-[8px] font-semibold tracking-[0.15em] uppercase text-gray-500">{v.size}</span>
            </div>
            <div className="flex flex-col items-center text-center">
              <ShieldCheck className="h-3.5 w-3.5 text-[#b91c1c] mb-1" />
              <span className="text-[8px] font-semibold tracking-[0.15em] uppercase text-gray-500">99%+</span>
            </div>
            <div className="flex flex-col items-center text-center">
              <Beaker className="h-3.5 w-3.5 text-[#b91c1c] mb-1" />
              <span className="text-[8px] font-semibold tracking-[0.15em] uppercase text-gray-500">HPLC</span>
            </div>
          </div>
        </div>

        <button
          onClick={handleAdd}
          aria-label="Add to cart"
          className="absolute bottom-0 left-0 right-0 z-30 bg-[#0a0a0a] text-white text-[11px] font-semibold tracking-[0.2em] uppercase py-3 translate-y-full group-hover:translate-y-0 transition-transform duration-300"
        >
          Add to Cart
        </button>
      </div>

      <div className="pt-4 pb-2 px-1">
        <div className="text-[10px] font-semibold tracking-[0.22em] uppercase text-gray-400 mb-1.5">
          {({ peptides: "Peptides", blends: "Blends", supplies: "Supplies" })[product.category] || "Catalog"}
        </div>
        <h3 className="font-display text-[17px] font-semibold text-[#0a0a0a] leading-tight">
          {product.name}
        </h3>
        <p className="text-[13px] text-gray-500 mt-1 line-clamp-1 font-light">{product.short}</p>
        <div className="mt-3 flex items-baseline justify-between">
          <div className="flex items-baseline gap-2">
            {minPrice === maxPrice ? (
              <span className="text-[15px] font-semibold text-[#0a0a0a]">{formatPrice(minPrice)}</span>
            ) : (
              <span className="text-[15px] font-semibold text-[#0a0a0a]">
                {formatPrice(minPrice)}<span className="text-gray-400">–</span>{formatPrice(maxPrice)}
              </span>
            )}
            {compare && compare > v.price && (
              <span className="text-[12px] text-gray-400 line-through">{formatPrice(compare)}</span>
            )}
          </div>
          <ArrowRight className="h-3.5 w-3.5 text-gray-400 group-hover:text-[#0a0a0a] group-hover:translate-x-0.5 transition-all" />
        </div>
      </div>
    </Link>
  );
};

export default ProductCard;
